import { useMutation } from '@tanstack/react-query';
import {
  Button as AntButton,
  Card,
  Col,
  Collapse,
  CollapseProps,
  Divider,
  Flex,
  Form,
  Input,
  MenuProps,
  Modal,
  Radio,
  Row,
  Skeleton,
  Space,
  Tooltip,
  Typography,
} from 'antd';
import { PaymentSummaryApis } from 'apis/PaymentSummaryApis';
import { Split } from 'assets/svg';
import { Button, InputForm, SelectForm } from 'components';
import {
  ExportOptionsButton,
  TMenuPropsCustom,
} from 'components/Button/ExportOptionsButton';
import { OptionsMenu } from 'components/OptionsMenu/OptionsMenu';
import { useTitle } from 'contexts/TitleProvider';
import Content from 'layouts/Content';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import path from 'routes/Path';
import { icons } from 'utils/constants/icons';
import TabSalesInvoice from './TabSalesInvoice';
import TabPurchaseInvoice from './TabPurchaseInvoice';
import TabSumInvoice from './TabSumInvoice';
import { useForm } from 'antd/es/form/Form';
import LazySelectForm, {
  ILazySelectData,
  ILazySelectParams,
  initLazySelectData,
} from 'components/Select/LazySelectForm';
import { CustomerApis } from 'apis/CustomerApis';
import { useAmlCheck } from 'hooks/useAmlCheck';
import { DefaultOptionType } from 'antd/es/select';
import { useLocation, useNavigate } from 'react-router-dom';
import formatNumber, {
  formatCurrency,
  formatDate,
  formatNumber_v2,
  handleOnChangeNumber,
  handleOnChangeNumberWithDecimal,
  toastError,
  toastSuccess,
  total,
} from 'utils/constants/functions';
import axiosGraphQL from 'apis/GraphQL/axiosGraphQL';
import axiosClient from 'apis/setup/axiosClient';
import DebtApis from 'apis/DebtApis';
import { useUser } from 'contexts/UserProvider';
import ModalExportHDDT from './ModalExportHDDT';
import DebtDetailModal from './DebtDetailModal';

export enum OrderDebitType {
  Q10 = 'SPO1_01',
  Amount = 'SPO1_02',
}

export enum PurchaseDebitType {
  Q10 = 'SPP1_01',
  Amount = 'SPP1_02',
  ReturnCustomer = 'SPP1_03',
}

interface IProps {
  sumPaymentId?: any;
  teamTypeId?: any;
  teamId?: any;
  customerId?: any;
  saleOrder?: any;
  purchaseOrder?: any;
}

// Helper functions để map statusId thành text và màu sắc
const getStatusText = (statusId: string) => {
  const statusMap: { [key: string]: string } = {
    BP100_01: 'Mới tạo',
    BP100_02: 'Đã Lưu',
    BP100_03: 'Khoá bảng THTT',
    BP100_04: 'Đã tạo công nợ',
    BP100_05: 'Kế toán điều chỉnh',
    BP100_06: 'Đã huỷ THTT',
  };
  return statusMap[statusId] || 'Mới tạo';
};

const getStatusColor = (statusId: string) => {
  // Tất cả đều màu xanh dương
  return '#1890ff';
};

const DetailPaymentSummaryPage = () => {
  const location = useLocation();

  const {
    sumPaymentId,
    isAdd,
    teamTypeId,
    teamId,
    customerId,
    saleOrder,
    purchaseOrder,
  } = location.state ?? {
    isAdd: true,
  };
  const { user } = useUser();
  const [form] = useForm();
  const navigate = useNavigate();
  const { PlusOutlined, CaretRightOutlined } = icons;
  const { setTitle, setBreadcrumb } = useTitle();
  const [customerData, setCustomersInRoute] =
    useState<ILazySelectData>(initLazySelectData);
  const firstRef = useRef(true);
  const [isUpdate, setIsUpdate] = useState(false);
  const [paymentSummaryDetail, setPaymentSummaryDetail] = useState<any>({});
  const [customerIdSelected, setCustomerIdSelected] = useState<any>(customerId);

  const [saleOrderSelected, setSaleOrderSelected] = useState<any>(
    saleOrder || [],
  );
  const [purchaseOrderSelected, setPurchaseOrderSelected] = useState<any>(
    purchaseOrder || [],
  );
  const [purchaseType, setPurchaseType] = useState<string>('');
  const [isOpenModalHDDT, setIsOpenModalHDDT] = useState(false);
  const [isAddState, setIsAddState] = useState(isAdd);
  const [purchaseOrdersData, setPurchaseOrdersData] = useState<any>([]);
  const [saleOrdersData, setSaleOrdersData] = useState<any>([]);
  const [sumPaymentData, setSumPaymentData] = useState<any>({});
  const [debtData, setDebtData] = useState<any>({});
  const [detailData, setDetailData] = useState<any>(null);
  const [isDebtCheckModalOpen, setIsDebtCheckModalOpen] = useState(false);
  const [debtCheckResult, setDebtCheckResult] = useState<any>(null);
  const [isLockModalOpen, setIsLockModalOpen] = useState(false);
  const [isEligibleToLock, setIsEligibleToLock] = useState(false);
  const [isEligibleForBilling, setIsEligibleForBilling] = useState(false);
  const [checkDebtForBilling, setCheckDebtForBilling] = useState(false);
  const [lockAction, setLockAction] = useState<'lock' | 'unlock' | null>(null);
  const [isDebtDetailModalOpen, setIsDebtDetailModalOpen] = useState(false);
  const [debtDetailData, setDebtDetailData] = useState<any>(null);
  const [debtCalculationData, setDebtCalculationData] = useState<any>(null);
  const [lockedDebtData, setLockedDebtData] = useState<any>(null);

  const { checkAndProceed, amlModalContext } = useAmlCheck();

  // Function to fetch debt data from APIs
  const fetchDebtData = async () => {
    // Lấy customerDebitId từ form (là mã khách hàng gán công nợ)
    const customerDebitId = form.getFieldValue('customerDebitId');

    if (!customerDebitId && !customerIdSelected) {
      return;
    }

    // Ưu tiên dùng customerDebitId, nếu không có thì dùng customerIdSelected
    const customerIdToUse = customerDebitId || customerIdSelected;

    try {
      // Check if table is locked
      if (detailData?.lockYn === true && sumPaymentId) {
        // Fetch locked debt data from getSumPaymentCustomerDebit API
        const lockedDebtResponse = await axiosGraphQL.post('/graphql', {
          query: `
query GetStatusTeamSale($input: TsumPaymentCustomerDebitWhereInput!) {
  getSumPaymentCustomerDebit(where: $input, orderBy: { createDate: DESC }) {
    items {
      id
      type
      customerDebitAmountLog
      customerDebitQ10Log
      pnjpDebitAmountLog
      pnjpDebitQ10Log
      cleanningYn
      goldThreadSalesLimit
      amountSalesLimit
      amountWagesTraditional
      dateWagesTraditional
      goldThreadQ10Traditional
      dateQ10Traditional
      amountHighendLimit
      dateHighendLimit
      amountHighendTraditionalLimit
      dateHighendTraditionalLimit
      effectiveTimeFrom
      effectiveTimeTo
      dateDebit
      ttype {
        id
        name
      }
      tsumPayment {
        id
        customerId
      }
    }
  }
}
          `,
          variables: {
            input: {
              sumPaymentId: { equals: sumPaymentId },
            },
          },
        });

        const lockedDebtItems =
          lockedDebtResponse?.data?.getSumPaymentCustomerDebit?.items || [];
        const lockedDebtItem = lockedDebtItems[0] || {};

        // Calculate remaining limits: limit - debt, if negative then 0
        const remainingQ10 = Math.max(
          0,
          (lockedDebtItem.goldThreadQ10Traditional || 0) -
            (lockedDebtItem.customerDebitQ10Log || 0),
        );
        // hạn mức nợ tiền công - tổng nợ tiền của kh
        const lockedType = lockedDebtItem?.type;
        const totalDebtAmount = lockedDebtItem.customerDebitAmountLog || 0;

        const rawTraditionalLimit = lockedDebtItem.amountWagesTraditional || 0;
        const rawPremiumLimit = lockedDebtItem.amountHighendLimit || 0;
        const rawPremiumTraditionalLimit =
          lockedDebtItem.amountHighendTraditionalLimit || 0;

        const remainingTraditionalLimit =
          lockedType === 'DEB10_01'
            ? Math.max(0, rawTraditionalLimit - totalDebtAmount)
            : rawTraditionalLimit;

        const remainingPremiumLimit =
          lockedType === 'DEB10_02'
            ? Math.max(0, rawPremiumLimit - totalDebtAmount)
            : rawPremiumLimit;

        const remainingPremiumTraditionalLimit =
          lockedType === 'DEB10_03'
            ? Math.max(0, rawPremiumTraditionalLimit - totalDebtAmount)
            : rawPremiumTraditionalLimit;

        const totalRemainingLimit = lockedDebtItem.amountHighendTraditionalLimit
          ? remainingPremiumTraditionalLimit
          : remainingTraditionalLimit + remainingPremiumLimit;

        const finalLockedDebtData = {
          pnjpDebitAmount: lockedDebtItem.pnjpDebitAmountLog || 0,
          pnjpDebitQ10: lockedDebtItem.pnjpDebitQ10Log || 0,
          pnjpHoldingAmount: 0,
          pnjpHoldingQ10: 0,
          customerDebitAmount: lockedDebtItem.customerDebitAmountLog || 0,
          customerDebitQ10: lockedDebtItem.customerDebitQ10Log || 0,
          remainingQ10: remainingQ10,
          traditionalLimit: remainingTraditionalLimit, // Hiển thị hạn mức còn lại thay vì hạn mức gốc
          premiumLimit: remainingPremiumLimit, // Hiển thị hạn mức còn lại thay vì hạn mức gốc
          premiumTraditionalLimit: remainingPremiumTraditionalLimit,
          totalLimit: totalRemainingLimit,
          goldThreadSalesLimit: lockedDebtItem.goldThreadSalesLimit || 0,
          amountSalesLimit: lockedDebtItem.amountSalesLimit || 0,
        };

        setLockedDebtData(finalLockedDebtData);
        return;
      }

      // Fetch data from getSumPaymentCustomerDebit API
      const debtResponse = await axiosGraphQL.post('/graphql', {
        query: `
query GetStatusTeamSale($input: TsumPaymentCustomerDebitWhereInput!) {
  getSumPaymentCustomerDebit(where: $input, orderBy: { createDate: DESC }) {
    items {
      id
      type
      customerDebitAmount
      customerDebitQ10
      pnjpDebitAmount
      pnjpDebitQ10
      cleanningYn
      tsumPayment { 
        id 
        customerId
      }
    }
  }
}
        `,
        variables: {
          input: {
            tsumPayment: { customerId: { equals: customerIdToUse } },
          },
        },
      });

      // Fetch data from getRuleCustomerDebitDetail API
      const limitResponse = await DebtApis.getRuleCustomerDebitDetail({
        customerId: customerIdToUse,
        plantId: user?.plantCode || '',
      });

      const debtItems =
        debtResponse?.data?.getSumPaymentCustomerDebit?.items || [];
      const latestDebt =
        debtItems.find((item: any) => item.cleanningYn === false) || {};
      const limitData = limitResponse?.data?.getRuleCustomerDebitDetail || {};

      const finalDebtData = {
        pnjpDebitAmount: latestDebt.pnjpDebitAmount || 0,
        pnjpDebitQ10: latestDebt.pnjpDebitQ10 || 0,
        pnjpHoldingAmount: 0,
        pnjpHoldingQ10: 0,
        customerDebitAmount: latestDebt.customerDebitAmount || 0,
        customerDebitQ10: latestDebt.customerDebitQ10 || 0,
        remainingQ10: limitData?.q10Tradable || 0,
        traditionalLimit: limitData?.vndTradable_01 || 0,
        premiumLimit: limitData?.vndTradable_02 || 0,
        premiumTraditionalLimit: limitData?.vndTradable_03 || 0,
        totalLimit: limitData?.vndTradable_03
          ? limitData.vndTradable_03
          : (limitData?.vndTradable_01 || 0) + (limitData?.vndTradable_02 || 0),
        goldThreadSalesLimit:
          limitData?.ruleCustomerDebit?.goldThreadSalesLimit || 0,
        amountSalesLimit: limitData?.ruleCustomerDebit?.amountSalesLimit || 0,
      };

      // Đảm bảo có một số data mặc định để hiển thị

      setDebtData(finalDebtData);
    } catch (error: any) {
      console.error('❌ Error fetching debt data:', error);
      console.error('❌ Error details:', {
        message: error?.message || 'Unknown error',
        response: error?.response?.data || null,
        status: error?.response?.status || null,
        config: error?.config || null,
      });
    }
  };

  const menuProps: MenuProps = {
    items: [
      {
        key: '1',
        label: 'Xuất excel',
        onClick: () => PaymentSummaryApis.exportExcel({ sumPaymentId }),
      },
      {
        key: '2',
        label: 'Xuất PDF',
        onClick: () => PaymentSummaryApis.exportPDF({ sumPaymentId }),
      },
      {
        key: '3',
        label: 'In',
        onClick: () => {
          // Print functionality placeholder
        },
      },
    ],
  };

  const optionMenuProps = {
    items: [
      {
        key: '1',
        label: <span className="text-[#DC362E]">Xóa</span>,
        onClick: () => mutateDeletePaymentSummary.mutate({ sumPaymentId }),
      },
      ...(detailData?.lockYn === true
        ? [
            {
              key: '2',
              label: 'Mở khoá THTT',
              onClick: () => {
                setLockAction('unlock');
                setIsLockModalOpen(true);
              },
            },
          ]
        : []),
    ],
  };

  // Debt check mutation
  const { mutate: checkDebt, isPending: checkingDebt } = useMutation({
    mutationFn: async (customerId: string) => {
      // Bước 1: Kiểm tra công nợ trong THTT trước
      let thttCheckResult = null;
      if (sumPaymentId) {
        try {
          const thttResponse = await axiosGraphQL.post('/graphql', {
            query: `
query CheckDebitInTHTT($input: CheckDebitTHTTInput!) {
  checkDebitInTHTT(input: $input) {
    code
    message
    success
    data {
      orderId
      message
    }
  }
}
            `,
            variables: {
              input: {
                sumPaymentId: sumPaymentId,
              },
            },
          });

          thttCheckResult = thttResponse?.data?.checkDebitInTHTT;

          // Nếu có lỗi từ THTT check, trả về kết quả này
          if (
            thttCheckResult?.success === true &&
            thttCheckResult?.code === '400'
          ) {
            return {
              thttCheck: thttCheckResult,
              hasTHTTError: true,
            };
          }
        } catch (error) {
          console.error('❌ Error checking THTT debt:', error);
          // Tiếp tục với logic cũ nếu THTT check lỗi
        }
      }

      // Bước 2: Logic kiểm tra công nợ hiện tại (giữ nguyên)
      const response = await DebtApis.getRuleCustomerDebitDetail({
        customerId,
        plantId: user?.plantCode || '',
      });

      return {
        ...(response?.data?.getRuleCustomerDebitDetail || {}),
        thttCheck: thttCheckResult,
        hasTHTTError: false,
      };
    },
    onSuccess: (result) => {
      setDebtCheckResult(result);

      // Kiểm tra nếu có lỗi từ THTT check
      if (result?.hasTHTTError === true) {
        setIsEligibleToLock(false);
        setIsEligibleForBilling(false);
        setIsDebtCheckModalOpen(true);
        return;
      }

      // Lấy tất cả dữ liệu từ sumPaymentData (đã được tính toán trong refetchSumPayment)
      const currentPNJPDebitQ10 = sumPaymentData?.pnjpDebitQ10 || 0;
      const currentPNJPDebitAmount = sumPaymentData?.pnjpDebitAmount || 0;
      const currentCustomerDebitQ10 = sumPaymentData?.customerDebitQ10 || 0;
      const currentCustomerDebitAmount =
        sumPaymentData?.customerDebitAmount || 0;

      // Các biến từ bảng "Tổng cộng (1) + (2) - (3)"
      const totalQ10Chi = sumPaymentData?.totalQ10Chi || 0;
      const totalHaolai = sumPaymentData?.totalHaolai || 0;
      const totalQ10Final = sumPaymentData?.totalQ10Final || 0;
      const finalPrice = sumPaymentData?.finalPrice || 0;

      console.log('🔍 Debug từ sumPaymentData:', {
        currentPNJPDebitQ10: currentPNJPDebitQ10,
        currentCustomerDebitQ10: currentCustomerDebitQ10,
        totalQ10Chi: totalQ10Chi,
        totalHaolai: totalHaolai,
        totalQ10Final: totalQ10Final,
        finalPrice: finalPrice,
      });

      // 3. Tính kết quả cuối cùng (Mục 1 + 2 - 3)
      const finalPNJPDebitQ10 = currentPNJPDebitQ10 + totalQ10Final;
      const finalPNJPDebitAmount = currentPNJPDebitAmount + finalPrice;

      // Lấy giá trị nợ khách hàng từ sumPaymentData (đã được tính toán trong refetchSumPayment)
      const finalCustomerDebitQ10 = sumPaymentData?.customerDebitQ10After || 0;
      const finalCustomerDebitAmount =
        sumPaymentData?.customerDebitAmountAfter || 0;

      // 4. Tính nợ mới = Kết quả cuối cùng - Nợ trước đó
      const newDebtQ10 = finalCustomerDebitQ10 - currentCustomerDebitQ10;
      const newDebtAmount =
        finalCustomerDebitAmount - currentCustomerDebitAmount;

      // 5. Lấy hạn mức còn lại để so sánh theo loại hàng tương ứng
      const remainingQ10Limit = result?.q10Tradable || 0; // 30.0000

      // Xác định hạn mức VND dựa trên loại hàng (type)
      let remainingAmountLimit = 0;
      if (detailData?.type === 'DEB10_01') {
        remainingAmountLimit = result?.vndTradable_01 || 0; // Truyền thống
      } else if (detailData?.type === 'DEB10_02') {
        remainingAmountLimit = result?.vndTradable_02 || 0; // Cao cấp
      } else if (detailData?.type === 'DEB10_03') {
        remainingAmountLimit = result?.vndTradable_03 || 0; // Loại khác
      } else {
        // Mặc định nếu không xác định được type
        remainingAmountLimit = result?.vndTradable_01 || 0;
      }

      // Tính hạn mức tối đa = hạn mức còn lại + công nợ hiện tại
      const maxAmountLimit = remainingAmountLimit + currentCustomerDebitAmount;
      const maxQ10Limit = remainingQ10Limit + currentCustomerDebitQ10;

      // 6. Kiểm tra hạn mức: |nợ mới| <= hạn mức còn lại
      const isEligibleAmount = Math.abs(newDebtAmount) <= remainingAmountLimit;

      // Chỉ kiểm tra hạn mức Q10 đối với hàng truyền thống (DEB10_01)
      // Các loại hàng khác (DEB10_02, DEB10_03) không cần kiểm tra Q10
      const isEligibleQ10 =
        detailData?.type === 'DEB10_01'
          ? Math.abs(newDebtQ10) <= remainingQ10Limit
          : true; // Không kiểm tra Q10 cho các loại hàng khác truyền thống

      // 7. Kiểm tra nợ mới không vượt quá hạn mức tối đa
      const isWithinMaxAmountLimit = Math.abs(newDebtAmount) <= maxAmountLimit;
      const isWithinMaxQ10Limit =
        detailData?.type === 'DEB10_01'
          ? Math.abs(newDebtQ10) <= maxQ10Limit
          : true; // Không kiểm tra Q10 cho các loại hàng khác truyền thống

      const isEligible =
        isEligibleQ10 &&
        isEligibleAmount &&
        isWithinMaxAmountLimit &&
        isWithinMaxQ10Limit;

      console.log(
        Math.abs(newDebtAmount),
        newDebtAmount,
        finalCustomerDebitAmount,
        currentCustomerDebitAmount,
      );
      console.log(sumPaymentData);

      // Lưu dữ liệu tính toán để hiển thị trong modal
      setDebtCalculationData({
        newDebtQ10,
        newDebtAmount,
        remainingQ10Limit,
        remainingAmountLimit,
        maxAmountLimit,
        maxQ10Limit,
        currentPNJPDebitQ10,
        currentPNJPDebitAmount,
        currentCustomerDebitQ10,
        currentCustomerDebitAmount,
        finalPNJPDebitQ10,
        finalPNJPDebitAmount,
        finalCustomerDebitQ10,
        finalCustomerDebitAmount,
      });

      // Chỉ set isEligibleToLock nếu KHÔNG kiểm tra cho billing
      if (!checkDebtForBilling) {
        setIsEligibleToLock(isEligible);
        // Luôn show modal khi kiểm tra công nợ cho khóa THTT
        setIsDebtCheckModalOpen(true);
      }

      // Set isEligibleForBilling nếu đang kiểm tra cho billing
      if (checkDebtForBilling) {
        setIsEligibleForBilling(isEligible);
        // Chỉ show modal cho billing nếu không đủ điều kiện
        if (!isEligible) {
          setIsDebtCheckModalOpen(true);
        }
      }

      // Reset flag
      setCheckDebtForBilling(false);
    },
    onError: (error) => {
      toastError('Lỗi khi kiểm tra công nợ: ' + error.message);
    },
  });

  // Debt detail mutation - fetch getSumPaymentCustomerDebit for popup
  const { mutate: checkDebtDetail, isPending: checkingDebtDetail } =
    useMutation({
      mutationFn: async (sumPaymentId: string) => {
        // Fetch sum payment customer debit for popup history
        const sumPaymentResponse = await axiosGraphQL.post('/graphql', {
          query: `
          query GetStatusTeamSale($input: TsumPaymentCustomerDebitWhereInput!) {
            getSumPaymentCustomerDebit(where: $input, orderBy: { createDate: DESC }) {
              items {
                id
                type
                customerDebitAmount
                customerDebitQ10
                pnjpDebitAmount
                pnjpDebitQ10
                customerDebitAmountLog
                customerDebitQ10Log
                pnjpDebitAmountLog
                pnjpDebitQ10Log
                cleanningYn
                goldThreadSalesLimit
                amountSalesLimit
                amountWagesTraditional
                dateWagesTraditional
                goldThreadQ10Traditional
                dateQ10Traditional
                amountHighendLimit
                dateHighendLimit
                amountHighendTraditionalLimit
                dateHighendTraditionalLimit
                effectiveTimeFrom
                effectiveTimeTo
                dateDebit
                ttype {
                  id
                  name
                }
                tsumPayment {
                  id
                  customerId
                }
              }
            }
          }
          `,
          variables: {
            input: {
              sumPaymentId: { equals: sumPaymentId },
            },
          },
        });

        return sumPaymentResponse?.data;
      },
      onSuccess: (result) => {
        setDebtDetailData(result);
        setIsDebtDetailModalOpen(true);
      },
      onError: (error) => {
        toastError('Lỗi khi tải chi tiết công nợ: ' + error.message);
      },
    });

  const fetchDebtDetail = () => {
    if (sumPaymentId) {
      checkDebtDetail(sumPaymentId);
    }
  };

  // Lock/Unlock THTT mutation
  const { mutate: toggleLockTHTT, isPending: lockingTHTT } = useMutation({
    mutationFn: async () => {
      return await axiosClient.post('/sap/unlock-thtt', { sumPaymentId });
    },
    onSuccess: () => {
      toastSuccess('Thành công!');
      setIsLockModalOpen(false);
      // Reload page
      window.location.reload();
    },
    onError: (error) => {
      toastError('Lỗi khi khoá/mở khoá THTT: ' + error.message);
    },
  });

  const mutateGetDetailTHTT = useMutation({
    mutationFn: PaymentSummaryApis.getDetailById,
    onSuccess: (res, variables) => {
      const detail = res?.data?.detail;
      setDetailData(detail); // Lưu detail data vào state
      if (
        firstRef.current &&
        ((detail?.saleOrder?.length > 0 && saleOrderSelected.length == 0) ||
          (detail?.purchaseOrder?.length > 0 &&
            purchaseOrderSelected.length == 0))
      ) {
        setSaleOrderSelected(detail.saleOrder);
        setPurchaseOrderSelected(detail.purchaseOrder);
        form.setFieldsValue({
          saleOrder: detail?.saleOrder,
          purchaseOrder: detail?.purchaseOrder,
        });
        return;
      }

      const dataNewSaleOrder = {
        ...res?.data?.saleOrder,
        ...(detail?.torderType?.id && { orderType: detail?.torderType?.id }),
        totalGroupSaleOrder: (
          res?.data?.saleOrder?.groupSaleOrder || []
        ).reduce(
          (prev: any, curr: any) => ({
            totalGoldWeightThread:
              prev.totalGoldWeightThread + curr?.totalGoldWeightThread || 0,
            totalq10: prev.totalq10 + curr?.totalq10 || 0,
            totalHaolai: prev.totalHaolai + curr?.totalHaolai || 0,
            totalq10T: prev.totalq10T + curr?.totalq10T || 0,
            totalUnitPrice: prev.totalUnitPrice + curr?.totalUnitPrice || 0,
            totalprocessPrice:
              prev.totalprocessPrice + curr?.totalprocessPrice || 0,
            totalFinalPrice: prev.totalFinalPrice + curr?.totalFinalPrice || 0,
          }),
          {
            totalGoldWeightThread: 0,
            totalq10: 0,
            totalHaolai: 0,
            totalq10T: 0,
            totalUnitPrice: 0,
            totalprocessPrice: 0,
            totalFinalPrice: 0,
          },
        ),
      };

      // res.data.purchaseOrder.groupPurchaseOrder =
      //   res?.data?.purchaseOrder?.groupPurchaseOrder.map((item: any) => {
      //     return {
      //       ...item,
      //       totalq10Accounting:
      //         typeof item?.totalq10Accounting !== 'number' ||
      //         item?.totalq10Accounting == 0
      //           ? null
      //           : item?.totalq10Accounting,
      //     };
      //   });
      let flatAccounting = false;
      const groupPurchaseOrders =
        res?.data?.purchaseOrder?.groupPurchaseOrder || [];

      // Sửa dữ liệu gốc trước khi tính tổng
      const modifiedGroupPurchaseOrders = groupPurchaseOrders.map(
        (cur: any) => {
          // Tự động gán TL Q10(chỉ) cho TL Q10 sau điều chỉnh(chỉ) khi giá trị sau điều chỉnh là 0 hoặc null
          if (
            typeof cur?.totalq10Accounting === 'number' &&
            cur?.totalq10Accounting > 0
          ) {
            flatAccounting = true;
            return cur; // Giữ nguyên nếu đã có giá trị điều chỉnh
          } else {
            // Nếu totalq10Accounting là 0, null hoặc undefined, gán bằng totalq10
            return {
              ...cur,
              totalq10Accounting: cur?.totalq10 || 0,
            };
          }
        },
      );

      const dataNewPurchaseOrder = {
        ...(detail?.tpurchaseType?.id && {
          purchaseType: detail?.tpurchaseType?.id,
        }),
        ...res?.data?.purchaseOrder,
        groupPurchaseOrder: modifiedGroupPurchaseOrders, // Sử dụng dữ liệu đã được sửa
        totalGroupPurchaseOrder: modifiedGroupPurchaseOrders.reduce(
          (pre: any, cur: any) => {
            const sumResult = {
              totalq10: pre.totalq10 + cur?.totalq10 || 0,
              totalq10Accounting:
                pre?.totalq10Accounting + (cur?.totalq10Accounting || 0),
              totalGoldWeightThread:
                pre.totalGoldWeightThread + cur?.totalGoldWeightThread || 0,
              totalFinalPrice:
                pre.totalFinalPrice +
                (cur?.finalPriceAccounting && cur?.finalPriceAccounting > 0
                  ? cur?.finalPriceAccounting
                  : cur?.totalFinalPrice || 0),
            };
            return sumResult;
          },
          {
            totalq10: 0,
            totalFinalPrice: 0,
            totalq10Accounting: 0,
            totalGoldWeightThread: 0,
          },
        ),
      };
      dataNewPurchaseOrder.flatAccounting = flatAccounting;

      setPaymentSummaryDetail(detail);
      setPurchaseOrdersData(dataNewPurchaseOrder);
      setSaleOrdersData(dataNewSaleOrder);

      if (firstRef.current) {
        // Tính totalQ10WithoutPayment để đảm bảo giá trị đúng
        const salesType = detail?.torderType?.id || OrderDebitType.Q10;
        const purchaseType = detail?.tpurchaseType?.id || PurchaseDebitType.Q10;

        // (1) Tổng công nợ: Q10 (chỉ) = Q10 khách hàng nợ - Q10 PNJP giữ hộ
        const totalQ10ChiFromDebt =
          (detail?.customerDebitQ10 || 0) - (detail?.pnjpDebitQ10 || 0);

        // (2) Bảng kê: Q10 (chỉ) = Q10(chỉ)
        const saleQ10Chi =
          !salesType || salesType == OrderDebitType.Q10
            ? dataNewSaleOrder?.totalGroupSaleOrder?.totalq10 || 0
            : 0;

        // (3) Bảng dẻ: Q10 (chỉ) = Q10 sau điều chỉnh(chỉ)
        const purchaseQ10Chi = (() => {
          const purchaseCondition =
            !purchaseType ||
            purchaseType == PurchaseDebitType.Q10 ||
            purchaseType == PurchaseDebitType.ReturnCustomer;
          return purchaseCondition
            ? dataNewPurchaseOrder?.totalGroupPurchaseOrder
                ?.totalq10Accounting || 0
            : 0;
        })();

        // Q10 (chỉ) = Q10 (chỉ) (1) + Q10 (chỉ) (2) - Q10 (chỉ) (3)
        const totalQ10Chi = totalQ10ChiFromDebt + saleQ10Chi - purchaseQ10Chi;

        // ===== CÁC BIẾN THEO THỨ TỰ BẢNG =====

        // 1. Q10 (chỉ) = Q10 (chỉ) (1) + Q10 (chỉ) (2) - Q10 (chỉ) (3)
        // (totalQ10Chi đã tính ở trên)

        // 2. Hao lai (chỉ) - chỉ cộng khi chọn nợ Q10
        const totalHaolai =
          !salesType || salesType == OrderDebitType.Q10
            ? dataNewSaleOrder?.totalGroupSaleOrder?.totalHaolai || 0
            : 0;

        // 3. Q10 tổng = Q10 (chỉ) + hao lai
        const totalQ10Final = totalQ10Chi + totalHaolai;

        // 4. Tiền Món
        const totalUnitPrice =
          dataNewSaleOrder?.totalGroupSaleOrder?.totalUnitPrice || 0;

        // 5. Tiền Công
        const totalprocessPrice =
          dataNewSaleOrder?.totalGroupSaleOrder?.totalprocessPrice || 0;

        // 6. Tiền vàng
        const totalGoldPrice =
          (!salesType || salesType == OrderDebitType.Amount
            ? dataNewSaleOrder?.totalGroupSaleOrder?.totalFinalPrice || 0
            : 0) -
          (!purchaseType ||
          purchaseType == PurchaseDebitType.Amount ||
          purchaseType == PurchaseDebitType.ReturnCustomer
            ? dataNewPurchaseOrder?.totalGroupPurchaseOrder?.totalFinalPrice ||
              0
            : 0);

        // 7. Tổng thanh toán (tiền mặt)
        const finalPrice =
          detail?.type === 'DEB10_01'
            ? (totalprocessPrice || 0) + // Tiền công: từ bảng kê
              (detail?.customerDebitAmount || 0) - // Khách hàng nợ (mục 1): từ API
              (detail?.pnjpDebitAmount || 0) // PNJP nợ (mục 1): từ API
            : (totalGoldPrice || 0) + // Tiền vàng: từ bảng kê và bảng dẻ
              (totalprocessPrice || 0) + // Tiền công: từ bảng kê
              (detail?.customerDebitAmount || 0) - // Khách hàng nợ (mục 1): từ API
              (detail?.pnjpDebitAmount || 0); // PNJP nợ (mục 1): từ API
        console.log('setSumPaymentData');
        setSumPaymentData({
          totalQ10: totalQ10Final, // Sử dụng giá trị tính toán đúng
          totalQ10Chi: totalQ10Chi, // Thêm trường Q10(chỉ) tổng cộng
          totalQ10Final: totalQ10Final, // Q10 tổng = Q10(chỉ) + hao lai
          totalHaolai: totalHaolai, // Hao lai từ bảng kê
          totalUnitPrice: totalUnitPrice, // Tính từ bảng kê
          totalprocessPrice: totalprocessPrice, // Tính từ bảng kê
          totalGoldPrice: totalGoldPrice, // Tính từ bảng kê - bảng dẻ
          finalPrice: finalPrice, // Tính theo công thức
          marketPrice: detail?.marketPrice || 0, // Lưu marketPrice từ API response
          pnjpDebitQ10After: detail?.pnjpDebitQ10After || 0,
          pnjpDebitAmountAfter: detail?.pnjpDebitAmountAfter || 0,
          customerDebitAmountAfter: detail?.customerDebitAmountAfter || 0,
          customerDebitQ10After: detail?.customerDebitQ10After || 0,
        });
        form.setFieldsValue({
          ...detail, // Load detail data first
          orderType: detail?.torderType?.id || OrderDebitType.Q10, // Map từ torderType.id
          purchaseType: detail?.tpurchaseType?.id || PurchaseDebitType.Q10, // Map từ tpurchaseType.id
          ...(!isAddState && {
            customerId: `${detail?.tcustomer?.sapCustomerId} ${detail?.tcustomer?.storeName && '- ' + detail?.tcustomer?.storeName}`,
          }),

          //Phương thức thanh toán
          q10Payment: checkIsZeroForInput(detail?.q10),
          codPrice: checkIsZeroForInput(detail?.codPrice),
          transferPrice: checkIsZeroForInput(detail?.transferPrice),
          voucher: checkIsZeroForInput(detail?.voucher),

          //Khách hàng nợ
          customerDebitAmount: checkIsZeroForInput(detail?.customerDebitAmount),
          customerDebitQ10: checkIsZeroForInput(detail?.customerDebitQ10),

          //PNJP nợ
          pnjpDebitAmount: checkIsZeroForInput(detail?.pnjpDebitAmount),
          pnjpDebitQ10: checkIsZeroForInput(detail?.pnjpDebitQ10),

          //PNJP nợ sau thanh toán
          pnjpDebitQ10After: checkIsZeroForInput(detail?.pnjpDebitQ10After),
          pnjpDebitAmountAfter: checkIsZeroForInput(
            detail?.pnjpDebitAmountAfter,
          ),

          //Khách hàng nợ sau thanh toán
          customerDebitQ10After: checkIsZeroForInput(
            detail?.customerDebitQ10After,
          ),
          customerDebitAmountAfter: checkIsZeroForInput(
            detail?.customerDebitAmountAfter,
          ),

          //Mã khách hàng gán công nợ
          customerDebitId: detail?.customerDebitId || customerIdSelected, // Nếu có từ detail thì dùng, không thì dùng customerIdSelected
        });
        firstRef.current = false;
      } else {
        // setSumPaymentData(
        //   refetchSumPayment(dataNewSaleOrder, dataNewPurchaseOrder),
        // );
        refetchSumPayment(
          dataNewSaleOrder,
          dataNewPurchaseOrder,
          false,
          detail,
        );
      }
      form.setFieldsValue({
        marketPrice: checkIsZeroForInput(detail?.marketPrice), //Giá bóng

        //Các trường khác
        purchaseDeductOrder: detail?.purchaseDeductOrder || 0,
        processPriceDeduct: detail?.processPriceDeduct || 0,
      });

      if (detail?.purchaseAcYn) {
        refetchSumPayment(dataNewSaleOrder, dataNewPurchaseOrder, true, detail);
        firstRef.current = false;
        return;
      }
    },
    onError: (error) => {
      // Handle error silently
    },
  });

  const { mutate: getCustomersMutate, isPending: isGetCustomersPending } =
    useMutation({
      mutationFn: (params: { pageSize: number; searchKey: string }) =>
        PaymentSummaryApis.getCustomerCheckInByPlant(params),
      // }) => CustomerApis.getCustomersInCombobox(params),
      onSuccess: (res) => {
        if (res?.data?.getCustomerBillInPlant) {
          const data = res?.data?.getCustomerBillInPlant;

          setCustomersInRoute({
            options: data.items
              .filter((item: any) => item?.sapCustomerId)
              .map(
                (item: any) =>
                  ({
                    label:
                      item?.sapCustomerId +
                      (item?.storeName ? ` - ${item.storeName}` : ''),
                    value: item?.id,
                    customer: item,
                  }) as DefaultOptionType,
              ),
            totalItems: data?.pageInfo?.totalItems,
          });
        }
      },
      onError: (error) => {
        // Handle error silently
      },
    });

  const mutateSavePaymentSummary = useMutation({
    mutationFn: PaymentSummaryApis.savePaymentSumary,
    onSuccess: (res, variables) => {
      if (isAddState) {
        setIsAddState(false);
        navigate(path.PAYMENT_SUMMARY_DETAIL, {
          state: {
            sumPaymentId: res?.data?.id,
            isAdd: false,
            teamTypeId: teamTypeId,
            teamId: teamId,
            customerId: customerIdSelected,
            saleOrder: saleOrderSelected,
            purchaseOrder: purchaseOrderSelected,
          },
        });
        mutateGetDetailTHTT.mutate({
          sumPaymentId: res?.data?.id,
          customerId: customerIdSelected,
          teamTypeId,
          teamId,
          saleOrder: saleOrderSelected || [],
          purchaseOrder: purchaseOrderSelected || [],
        });
      } else if (variables.isCalcAgain) {
        // refectGetDetailTHTT();
        toastSuccess('Đã cập nhật xong');
        return;
      } else {
        setPurchaseOrderSelected([...purchaseOrderSelected]);
      }
      toastSuccess('Lưu thành công');
    },
    onError: (error) => {
      toastError('Lưu thất bại');
    },
  });

  const mutateDeletePaymentSummary = useMutation({
    mutationFn: PaymentSummaryApis.deletePaymentSummary,
    onSuccess: (res, variables) => {
      toastSuccess('Xóa thành công');
      navigate(path.PAYMENT_SUMMARY_V2);
    },
    onError: (error) => {
      toastError('Xóa thất bại');
    },
  });

  // Reset eligible status when customer changes
  useEffect(() => {
    setIsEligibleToLock(false);
    setIsEligibleForBilling(false);
  }, [customerIdSelected]);

  const refectGetDetailTHTT = useCallback(() => {
    if (customerIdSelected) {
      mutateGetDetailTHTT.mutate({
        sumPaymentId,
        customerId: customerIdSelected,
        teamTypeId,
        teamId,
        saleOrder: saleOrderSelected || [],
        purchaseOrder: purchaseOrderSelected || [],
      });
    } else {
      mutateGetDetailTHTT.mutate({
        sumPaymentId,
        customerId: undefined,
        teamTypeId,
        teamId,
        saleOrder: [],
        purchaseOrder: [],
      });
    }
  }, [customerIdSelected, purchaseOrderSelected, saleOrderSelected]);

  useEffect(() => {
    setTitle('Bảng Tổng hợp thanh toán');
    setBreadcrumb([
      { title: 'Kế toán/Thủ quỹ' },
      { title: 'Bảng Tổng hợp thanh toán', href: path.PAYMENT_SUMMARY_V2 },
      { title: 'Chi tiết bảng Tổng hợp thanh toán' },
    ]);
  }, [setBreadcrumb, setTitle]);

  useEffect(() => {
    refectGetDetailTHTT();
  }, [refectGetDetailTHTT]);

  const classNamePanel = 'bg-white !rounded-lg !shadow-md mt-[14px]';
  const items: CollapseProps['items'] = [
    {
      key: '1',
      classNames: { body: '!pt-0' },
      label: (
        <Row justify={'space-between'}>
          <Col>
            <Typography.Text className="text-base font-bold">
              2. Bảng kê
            </Typography.Text>
          </Col>
          <Col>Tiền tệ: VND</Col>
        </Row>
      ),
      children: (
        <TabSalesInvoice
          form={form}
          setSaleOrderSelected={setSaleOrderSelected}
          saleOrderSelected={saleOrderSelected}
          saleOrdersData={saleOrdersData}
          customerId={customerIdSelected}
          loading={mutateGetDetailTHTT.isPending}
          refetchSumPayment={refetchSumPayment}
          lockYn={detailData?.lockYn}
          purchaseType={purchaseType}
        />
      ),
      className: classNamePanel,
    },
    {
      key: '2',
      classNames: { body: '!pt-0' },
      label: (
        <Row justify={'space-between'}>
          <Col>
            <Typography.Text className="text-base font-bold">
              3. Bảng dẻ
            </Typography.Text>
          </Col>
          <Col>Tiền tệ: VND</Col>
        </Row>
      ),
      children: (
        <TabPurchaseInvoice
          form={form}
          customerId={customerIdSelected}
          purchaseOrderSelected={purchaseOrderSelected}
          purchaseOrdersData={purchaseOrdersData}
          setPurchaseOrderSelected={setPurchaseOrderSelected}
          setSaleOrderSelected={setSaleOrderSelected}
          setPurchaseType={setPurchaseType}
          refetchSumPayment={refetchSumPayment}
          // customerId={customerIdSelected}
          loading={mutateGetDetailTHTT.isPending}
          lockYn={detailData?.lockYn}
        />
      ),
      className: classNamePanel,
    },
    {
      key: '3',
      label: (
        <Row justify={'space-between'}>
          <Col>
            <Typography.Text className="text-base font-bold">
              Tổng cộng (1) + (2) - (3)
            </Typography.Text>
          </Col>
          <Col>Tiền tệ: VND</Col>
        </Row>
      ),
      children: (
        <TabSumInvoice
          form={form}
          customerId={customerIdSelected}
          sumPaymentData={sumPaymentData}
          setSumPaymentData={setSumPaymentData}
          refetchSumPayment={refetchSumPayment}
          loading={mutateGetDetailTHTT.isPending}
          lockYn={detailData?.lockYn}
          detailData={detailData}
          fetchDebtData={fetchDebtData}
          setSaleOrderSelected={setSaleOrderSelected}
          setPurchaseOrderSelected={setPurchaseOrderSelected}
        />
      ),
      className: classNamePanel,
    },
  ];

  const checkIsZero = (value: any, isQ10 = false) => {
    if (value == 0 || !value) {
      return undefined;
    }
    return formatNumber(value);
    // return isQ10 ? formatNumber_v2(value) : formatNumber(value);
  };

  const checkIsZeroForInput = (value: any) => {
    if (value == 0 || !value) {
      return undefined;
    }
    return Number(value);
  };

  function refetchSumPayment(
    dataNewSaleOrder: any = saleOrdersData,
    dataNewPurchaseOrder: any = purchaseOrdersData,
    isCalcAgain = false,
    detailData: any = paymentSummaryDetail,
  ) {
    const dataForm = form.getFieldsValue();
    Object.keys(dataForm).forEach((key) => {
      if (
        key.includes('note') ||
        key.includes('orderType') ||
        key.includes('purchaseType')
      )
        return;
      const valueWithoutDots =
        dataForm[key] &&
        dataForm[key].toString().replace(/\./g, '').replace(',', '.');
      dataForm[key] = parseFloat(valueWithoutDots);
    });

    dataNewSaleOrder.orderType = dataForm?.orderType;
    dataNewPurchaseOrder.purchaseType = dataForm?.purchaseType;

    const salesType = dataForm?.orderType; // Lấy từ torderType.id
    const purchaseType = dataForm?.purchaseType; // Lấy từ tpurchaseType.id
    // totalQ10 cho bảng "Tổng cộng (1) + (2) - (3)" - KHÔNG bao gồm phương thức thanh toán
    // (1) Tổng công nợ: Q10 (chỉ) = Q10 khách hàng nợ - Q10 PNJP giữ hộ
    const totalQ10ChiFromDebt =
      (parseFloat(displayDebtData?.customerDebitQ10) || 0) -
      (parseFloat(displayDebtData?.pnjpDebitQ10) || 0);

    // (2) Bảng kê: Q10 (chỉ) = Q10(chỉ)
    const saleQ10Chi =
      !salesType || salesType == OrderDebitType.Q10
        ? dataNewSaleOrder?.totalGroupSaleOrder?.totalq10 || 0
        : 0;

    // (3) Bảng dẻ: Q10 (chỉ) = Q10 sau điều chỉnh(chỉ)
    const purchaseQ10Chi = (() => {
      const purchaseCondition =
        !purchaseType ||
        purchaseType == PurchaseDebitType.Q10 ||
        purchaseType == PurchaseDebitType.ReturnCustomer;
      return purchaseCondition
        ? dataNewPurchaseOrder?.totalGroupPurchaseOrder?.totalq10Accounting || 0
        : 0;
    })();

    // ===== CÁC BIẾN THEO THỨ TỰ BẢNG =====

    // 1. Q10 (chỉ) = Q10 (chỉ) (1) + Q10 (chỉ) (2) - Q10 (chỉ) (3)
    const totalQ10Chi = totalQ10ChiFromDebt + saleQ10Chi - purchaseQ10Chi;

    // 2. Hao lai (chỉ)
    const totalHaolai =
      !salesType || salesType == OrderDebitType.Q10
        ? dataNewSaleOrder?.totalGroupSaleOrder?.totalHaolai || 0
        : 0;

    // 3. Q10 tổng = Q10 (chỉ) + hao lai
    const totalQ10Final = totalQ10Chi + totalHaolai;

    // 4. Tiền Món
    const totalUnitPrice =
      dataNewSaleOrder?.totalGroupSaleOrder?.totalUnitPrice;

    // 5. Tiền Công
    const totalprocessPrice =
      dataNewSaleOrder?.totalGroupSaleOrder?.totalprocessPrice;

    // 6. Tiền vàng
    const totalGoldPrice =
      (!salesType || salesType == OrderDebitType.Amount
        ? dataNewSaleOrder?.totalGroupSaleOrder?.totalFinalPrice
        : 0) -
      (!purchaseType ||
      purchaseType == PurchaseDebitType.Amount ||
      purchaseType == PurchaseDebitType.ReturnCustomer
        ? dataNewPurchaseOrder?.totalGroupPurchaseOrder?.totalFinalPrice
        : 0);

    // 7. Tổng thanh toán (tiền mặt)
    const finalPrice =
      detailData?.type === 'DEB10_01'
        ? (totalprocessPrice || 0) + // Tiền công: từ bảng kê
          (displayDebtData?.customerDebitAmount || 0) - // Khách hàng nợ (mục 1): từ sidebar
          (displayDebtData?.pnjpDebitAmount || 0) // PNJP nợ (mục 1): từ sidebar
        : (totalGoldPrice || 0) + // Tiền vàng: từ bảng kê và bảng dẻ
          (totalprocessPrice || 0) + // Tiền công: từ bảng kê
          (displayDebtData?.customerDebitAmount || 0) - // Khách hàng nợ (mục 1): từ sidebar
          (displayDebtData?.pnjpDebitAmount || 0); // PNJP nợ (mục 1): từ sidebar

    // ===== CÁC BIẾN KHÁC =====

    // 8. Phương thức thanh toán -> Tiền mặt (COD)
    const codPrice = parseFloat(dataForm?.codPrice) || 0;

    // 9. Phương thức thanh toán -> Q10
    const q10Payment = parseFloat(dataForm?.q10Payment) || 0;

    // 10. Phương thức thanh toán -> Chuyển khoản
    const transferPrice = parseFloat(dataForm?.transferPrice) || 0;

    // 11. Phương thức thanh toán -> Voucher
    const voucher = parseFloat(dataForm?.voucher) || 0;

    // Q10 tổng để tính toán = 3. Q10 tổng - Q10 payment
    const totalQ10ForCalculation = totalQ10Final - q10Payment;

    // Tiền mặt để tính toán = 7. Tổng thanh toán - Phương thức thanh toán
    const totalAmountForCalculation =
      (finalPrice || 0) - (codPrice + transferPrice + voucher);

    // 12. PNJP nợ -> Q10 tổng = nếu Q10 tổng để tính toán > 0 ? 0 : ABS(Q10 tổng để tính toán)
    const pnjpDebitQ10After =
      totalQ10ForCalculation > 0 ? 0 : Math.abs(totalQ10ForCalculation);

    // 13. PNJP nợ -> Tiền mặt = nếu Tiền mặt để tính toán > 0 ? 0 : ABS(Tiền mặt để tính toán)
    const pnjpDebitAmountAfter =
      totalAmountForCalculation > 0 ? 0 : Math.abs(totalAmountForCalculation);

    // 14. Khách hàng nợ -> Q10 tổng = nếu Q10 tổng để tính toán > 0 ? ABS(Q10 tổng để tính toán) : 0
    const customerDebitQ10After =
      totalQ10ForCalculation > 0 ? Math.abs(totalQ10ForCalculation) : 0;

    // 15. Khách hàng nợ -> Tiền mặt = nếu Tiền mặt để tính toán > 0 ? ABS(Tiền mặt để tính toán) : 0
    const customerDebitAmountAfter =
      totalAmountForCalculation > 0 ? Math.abs(totalAmountForCalculation) : 0;

    // ===== 4 TRƯỜNG MỚI CẦN LƯU =====

    // orderTotalPrice: thành tiền công hoặc thành tiền món => cái nào có thì lưu
    const orderTotalPrice = (() => {
      if (totalprocessPrice && totalprocessPrice > 0) {
        return totalprocessPrice; // Thành tiền công
      } else if (totalUnitPrice && totalUnitPrice > 0) {
        return totalUnitPrice; // Thành tiền món
      }
      return 0;
    })();

    // orderTotalQ10: Q10 tổng từ bảng kê
    const orderTotalQ10 = dataNewSaleOrder?.totalGroupSaleOrder?.totalq10T || 0;

    // purchaseTotalPrice: Thành tiền vàng từ bảng dẻ
    const purchaseTotalPrice = (() => {
      return (
        dataNewPurchaseOrder?.totalGroupPurchaseOrder?.totalFinalPrice || 0
      );
    })();

    // purchaseTotalQ10: TL Q10 sau điều chỉnh từ bảng dẻ
    const purchaseTotalQ10 = (() => {
      return (
        dataNewPurchaseOrder?.totalGroupPurchaseOrder?.totalq10Accounting || 0
      );
    })();

    // Set các biến vào form - nếu = 0 hoặc null thì set rỗng
    form.setFieldsValue({
      totalQ10Chi: totalQ10Chi || undefined,
      totalHaolai: totalHaolai || undefined,
      totalQ10Final: totalQ10Final || undefined,
      totalUnitPrice: totalUnitPrice || undefined,
      totalprocessPrice: totalprocessPrice || undefined,
      totalGoldPrice: totalGoldPrice || undefined,
      finalPrice: finalPrice || undefined,
      codPrice: codPrice || undefined,
      q10Payment: q10Payment || undefined,
      transferPrice: transferPrice || undefined,
      voucher: voucher || undefined,
      pnjpDebitQ10After: pnjpDebitQ10After || undefined,
      pnjpDebitAmountAfter: pnjpDebitAmountAfter || undefined,
      customerDebitQ10After: customerDebitQ10After || undefined,
      customerDebitAmountAfter: customerDebitAmountAfter || undefined,
    });

    setSaleOrdersData(dataNewSaleOrder);
    setPurchaseOrdersData(dataNewPurchaseOrder);
    setSumPaymentData({
      // Các biến từ bảng "Tổng cộng (1) + (2) - (3)"
      totalQ10: totalQ10Final, // Dùng totalQ10Final cho bảng
      totalQ10Chi: totalQ10Chi, // 1. Q10 (chỉ)
      totalQ10Final: totalQ10Final, // 3. Q10 tổng = Q10(chỉ) + hao lai
      totalHaolai, // 2. Hao lai (chỉ)
      totalUnitPrice, // 4. Tiền Món
      totalprocessPrice, // 5. Tiền Công
      totalGoldPrice, // 6. Tiền vàng
      finalPrice, // 7. Tổng thanh toán (tiền mặt)

      // Phương thức thanh toán
      codPrice, // 8. Phương thức thanh toán -> Tiền mặt (COD)
      q10Payment, // 9. Phương thức thanh toán -> Q10
      transferPrice, // 10. Phương thức thanh toán -> Chuyển khoản
      voucher, // 11. Phương thức thanh toán -> Voucher

      // Kết quả sau thanh toán
      pnjpDebitQ10After, // 12. PNJP nợ -> Q10 tổng
      pnjpDebitAmountAfter, // 13. PNJP nợ -> Tiền mặt
      customerDebitQ10After, // 14. Khách hàng nợ -> Q10 tổng
      customerDebitAmountAfter, // 15. Khách hàng nợ -> Tiền mặt

      // Data từ mục 1 để tính Q10 tổng
      customerDebitQ10: displayDebtData?.customerDebitQ10 || 0,
      pnjpDebitQ10: displayDebtData?.pnjpDebitQ10 || 0,
      marketPrice: detailData?.marketPrice || 0, // Lấy marketPrice từ param truyền vào

      // 4 trường mới cần lưu
      orderTotalPrice, // Thành tiền công hoặc thành tiền món từ bảng kê
      orderTotalQ10, // Q10 tổng từ bảng kê
      purchaseTotalPrice, // Thành tiền vàng từ bảng dẻ
      purchaseTotalQ10, // TL Q10 sau điều chỉnh từ bảng dẻ
    });

    isCalcAgain && setIsUpdate(true);
  }

  useEffect(() => {
    if (isUpdate) {
      onFinish(form.getFieldsValue(), true);
      setIsUpdate(false);
    }
  }, [isUpdate]);

  useEffect(() => {
    fetchDebtData();
  }, [customerIdSelected, detailData?.lockYn, sumPaymentId]);

  const handleCustomerSelectScrollChange = useCallback(
    (params: ILazySelectParams) => {
      const { pageSize, value } = params;

      getCustomersMutate({
        pageSize,
        // plantId: user?.isAdmin ? undefined : user?.plantCode,
        searchKey: value || '',
      });
    },
    [form],
  );

  const checkFormValidate = async () => {
    try {
      await form.validateFields();
      return true;
    } catch (error) {
      return false;
    }
  };

  const onFinish = async (values: any, isCalcAgain = false) => {
    const option = customerData?.options?.find(
      (opt: any) => opt.value === values.customerId,
    ) as any;
    const selectedCustomer = option?.customer || { id: values.customerId };
    checkAndProceed(selectedCustomer, () => {
      handleSavePaymentSummary(values, isCalcAgain);
    });
  };

  const handleSavePaymentSummary = async (values: any, isCalcAgain = false) => {
    // Debug form values
    console.log('🔍 onFinish values:', values);
    console.log('🔍 form.getFieldsValue():', form.getFieldsValue());
    if (!(await checkFormValidate())) {
      toastError('Vui lòng nhập đầy đủ thông tin');
      return;
    }

    const valueNumber: { [key: string]: any } = {};

    Object.keys(values).forEach((key) => {
      if (
        key == 'pnjpDebitAmount' ||
        key == 'pnjpDebitQ10' ||
        key == 'customerDebitAmount' ||
        key == 'customerDebitQ10' ||
        key == 'codPrice' ||
        key == 'transferPrice' ||
        key == 'purchaseDeductOrder' ||
        key == 'processPriceDeduct' ||
        key == 'q10' ||
        key == 'q10Payment' ||
        key == 'voucher' ||
        key == 'pnjpDebitAmountAfter' ||
        key == 'pnjpDebitQ10After' ||
        key == 'customerDebitAmountAfter' ||
        key == 'customerDebitQ10After'
      ) {
        // values[key] =
        //   value && value.toString().replace(/\./g, '').replace(',', '.');
        valueNumber[key] = values[key]
          ? parseFloat(
              values[key].toString().replace(/\./g, '').replace(',', '.'),
            )
          : 0;
      }
    });

    // customerDebitId từ form đã là mã DMS rồi (sau khi sửa logic)
    // Không cần convert nữa vì đã lưu trực tiếp mã DMS vào form
    const customerDebitDmsId = values.customerDebitId;

    // Debug dữ liệu được gửi lên API
    console.log('🔍 Debug before finalData:', sumPaymentData);

    const finalData = {
      ...values,
      ...valueNumber, // valueNumber sẽ ghi đè values cho các trường đã parse
      sumPaymentId,
      ...sumPaymentData,
      marketPrice: sumPaymentData.marketPrice || 0, // Đảm bảo marketPrice từ API được truyền vào
      teamSaleId: teamId,
      customerId: customerIdSelected, // Mã khách hàng chính của THTT
      isCalcAgain,
      type: detailData?.type || 'DEB10_01', // Pass type from detail.type or default to DEB10_01
      // Mapping q10Payment từ form sang q10 cho API
      q10: valueNumber.q10Payment || 0,
      // Đảm bảo các trường After luôn là số - lấy từ sumPaymentData (đã tính toán)
      customerDebitAmountAfter: sumPaymentData?.customerDebitAmountAfter || 0,
      pnjpDebitQ10After: sumPaymentData?.pnjpDebitQ10After || 0,
      pnjpDebitAmountAfter: sumPaymentData?.pnjpDebitAmountAfter || 0,
      customerDebitQ10After: sumPaymentData?.customerDebitQ10After || 0,
      // 4 trường mới cần lưu - lấy từ sumPaymentData (đã tính toán)
      orderTotalPrice: sumPaymentData?.orderTotalPrice || 0,
      orderTotalQ10: sumPaymentData?.orderTotalQ10 || 0,
      purchaseTotalPrice: sumPaymentData?.purchaseTotalPrice || 0,
      purchaseTotalQ10: sumPaymentData?.purchaseTotalQ10 || 0,
      // Customer ID mapping - đặt cuối để không bị ghi đè
      customerDebitId: customerDebitDmsId, // Mã khách hàng gán công nợ (DMS ID)
    };

    console.log('🔍 Debug final data:', {
      customerDebitAmountAfter: {
        value: finalData.customerDebitAmountAfter,
        type: typeof finalData.customerDebitAmountAfter,
      },
      pnjpDebitQ10After: {
        value: finalData.pnjpDebitQ10After,
        type: typeof finalData.pnjpDebitQ10After,
      },
      pnjpDebitAmountAfter: {
        value: finalData.pnjpDebitAmountAfter,
        type: typeof finalData.pnjpDebitAmountAfter,
      },
      customerDebitQ10After: {
        value: finalData.customerDebitQ10After,
        type: typeof finalData.customerDebitQ10After,
      },
      // Debug customer ID mapping
      customerIdMapping: {
        customerId: finalData.customerId, // Mã khách hàng chính của THTT
        customerDebitId: finalData.customerDebitId, // Mã khách hàng gán công nợ (DMS ID)
        customerIdSelected: customerIdSelected, // Mã DMS từ state
        dmsIdFromForm: values.customerDebitId, // Mã DMS từ form (sau khi sửa)
        dmsIdConverted: customerDebitDmsId, // Mã DMS đã convert
      },
      // Debug finalData structure
      finalDataKeys: Object.keys(finalData),
      finalDataCustomerDebitId: finalData.customerDebitId,
    });
    console.log('finalData', finalData);
    mutateSavePaymentSummary.mutate(finalData);
  };

  // Determine which debt data to display based on lock status
  const displayDebtData = useMemo(() => {
    if (detailData?.lockYn === true && lockedDebtData) {
      return lockedDebtData;
    }
    return debtData;
  }, [detailData?.lockYn, lockedDebtData, debtData]);

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      onValuesChange={refetchSumPayment}
    >
      {amlModalContext}
      <Row justify={'space-between'} gutter={[6, 12]} align="middle">
        <Col>
          <div style={{ textAlign: 'center' }}>
            <Typography.Text
              className="text-sm font-medium"
              style={{
                color: '#666666', // Màu xám cho "Trạng thái:"
              }}
            >
              Trạng thái:{' '}
            </Typography.Text>
            <Typography.Text
              className="text-sm font-medium"
              style={{
                color: getStatusColor(detailData?.statusId),
              }}
            >
              {getStatusText(detailData?.statusId)}
            </Typography.Text>
          </div>
        </Col>
        <Col>
          <Row gutter={[6, 12]}>
            {(user?.isAdmin || user?.isAccountant) && (
              <>
                <Col>
                  <Button
                    fill
                    title="Tạo mới"
                    customClass={
                      detailData?.lockYn === true
                        ? '!bg-gray-400 !border-gray-400'
                        : `border-white ${!isAddState ? '' : '!bg-[#0077CC]'}`
                    }
                    disabled={detailData?.lockYn === true}
                    loading={mutateGetDetailTHTT.isPending}
                    type="button"
                  />
                  {/* Debug: {detailData?.lockYn ? 'LOCKED' : 'UNLOCKED'} */}
                </Col>
                <Col>
                  <Button
                    fill
                    title="Lưu"
                    customClass={
                      detailData?.lockYn === true
                        ? '!bg-gray-400 !border-gray-400'
                        : `border-white ${isAddState ? '' : '!bg-[#038F00]'}`
                    }
                    disabled={detailData?.lockYn === true}
                    loading={mutateGetDetailTHTT.isPending}
                  />
                </Col>
              </>
            )}
            {(user?.isAdmin || user?.isAccountant) && !isAddState && (
              <>
                <Col>
                  <Button
                    fill
                    title="Kiểm tra công nợ"
                    customClass={
                      detailData?.lockYn === true
                        ? '!bg-gray-400 !border-gray-400'
                        : '!bg-[#FFBF1F] border-white'
                    }
                    type="button"
                    disabled={detailData?.lockYn === true}
                    loading={checkingDebt}
                    handleOnclick={() => {
                      if (customerIdSelected) {
                        checkDebt(customerIdSelected);
                      } else {
                        toastError('Vui lòng chọn khách hàng');
                      }
                    }}
                  />
                  {/* Debug: {detailData?.lockYn ? 'LOCKED' : 'UNLOCKED'} */}
                </Col>
                <Col>
                  <Button
                    fill
                    title="Khoá bảng THTT"
                    customClass={
                      isEligibleToLock
                        ? '!bg-[#038F00] border-white'
                        : '!bg-gray-400 border-white'
                    }
                    type="button"
                    disabled={!isEligibleToLock}
                    loading={lockingTHTT}
                    handleOnclick={() => {
                      const currentLockStatus = detailData?.lockYn;

                      if (currentLockStatus === true) {
                        // Currently locked -> Unlock -> Show confirmation modal
                        setLockAction('unlock');
                        setIsLockModalOpen(true);
                      } else {
                        // Currently unlocked -> Lock -> Direct API call
                        setLockAction('lock');
                        toggleLockTHTT();
                      }
                    }}
                  />
                </Col>
                <Col>
                  <Button
                    fill
                    title="Xuất HĐĐT"
                    customClass={
                      detailData?.lockYn === true
                        ? '!bg-[#FFBF1F] border-white'
                        : '!bg-gray-400 !border-gray-400'
                    }
                    type="button"
                    disabled={detailData?.lockYn !== true}
                    loading={mutateGetDetailTHTT.isPending}
                    handleOnclick={() => setIsOpenModalHDDT(true)}
                  />
                </Col>
                <Col>
                  <ExportOptionsButton menu={menuProps} />
                </Col>
                <Col>
                  <OptionsMenu
                    menu={optionMenuProps}
                    placement="bottomRight"
                    className="bg-white !p-2 mt-1 border rounded-lg"
                  />
                </Col>
              </>
            )}
          </Row>
        </Col>
      </Row>
      <Row gutter={[16, 12]}>
        <Col span={6}>
          <Content>
            <Space direction="vertical" size="small" className="w-full">
              <div>
                <p>Bảng tổng hợp thanh toán</p>
                <Typography.Text className="text-base font-bold">
                  {isAddState ? '-' : paymentSummaryDetail?.id || '-'}
                </Typography.Text>
              </div>

              <div>
                <Form.Item
                  name="customerId"
                  layout="vertical"
                  className="mb-0 w-full"
                  label="Khách hàng"
                  // rules={[{ required: true, message: '' }]}
                >
                  <LazySelectForm
                    data={customerData}
                    loading={
                      isGetCustomersPending || mutateGetDetailTHTT.isPending
                    }
                    placeholder="Tìm MST/Mã khách hàng"
                    onSelectScrollChange={handleCustomerSelectScrollChange}
                    onChange={(value, option: any) => {
                      if (value) {
                        checkAndProceed(
                          option?.customer || { id: value },
                          () => {
                            setCustomerIdSelected(value);
                            setSaleOrderSelected([]);
                            setPurchaseOrderSelected([]);

                            form.setFieldsValue({
                              saleOrder: [],
                              purchaseOrder: [],
                              pnjpDebitQ10After: undefined,
                              pnjpDebitAmountAfter: undefined,
                              customerDebitAmountAfter: undefined,
                              customerDebitQ10After: undefined,
                            });
                          },
                        );
                      }
                    }}
                    disabled={!isAddState}
                  />
                </Form.Item>
              </div>

              <div>
                <p>Giá bóng</p>
                <Typography.Text className="text-base font-bold">
                  {!paymentSummaryDetail?.marketPrice
                    ? '-'
                    : formatNumber(paymentSummaryDetail?.marketPrice)}
                </Typography.Text>
              </div>
            </Space>
          </Content>
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              padding: '16px',
              marginTop: '14px',
            }}
          >
            <Space className="w-full" direction="vertical" size="small">
              <Typography.Text className="text-lg font-bold">
                1. Tổng công nợ
              </Typography.Text>

              <Space direction="vertical" className="w-full">
                <Typography.Text className="text-base font-bold">
                  PNJP Nợ
                </Typography.Text>
                <Flex justify={'space-between'} gap={10} className="w-full">
                  <p className="max-w-max">VND</p>
                  <Typography.Text className="flex-1 text-right">
                    {formatNumber(displayDebtData?.pnjpDebitAmount || 0)}
                  </Typography.Text>
                </Flex>
              </Space>

              <Divider style={{ margin: '12px 0' }} />

              <Space direction="vertical" className="w-full">
                <Typography.Text className="text-base font-bold">
                  PNJP giữ hộ
                </Typography.Text>
                <Flex justify={'space-between'} gap={10} className="w-full">
                  <p className="max-w-max">Q10</p>
                  <Typography.Text className="flex-1 text-right">
                    {formatCurrency({
                      value: Number(
                        Number(displayDebtData?.pnjpDebitQ10 || 0).toFixed(4),
                      ),
                      format: 4,
                    })}
                  </Typography.Text>
                </Flex>
              </Space>

              <Divider style={{ margin: '12px 0' }} />

              <Space direction="vertical" className="w-full">
                <Typography.Text className="text-base font-bold">
                  Khách hàng nợ
                </Typography.Text>
                <Flex justify={'space-between'} gap={10} className="w-full">
                  <p className="max-w-max">Q10</p>
                  <Typography.Text className="flex-1 text-right">
                    {formatCurrency({
                      value: Number(
                        Number(displayDebtData?.customerDebitQ10 || 0).toFixed(
                          4,
                        ),
                      ),
                      format: 4,
                    })}
                  </Typography.Text>
                </Flex>
                <Flex justify={'space-between'} gap={10} className="w-full">
                  <p className="max-w-max">VND</p>
                  <Typography.Text className="flex-1 text-right">
                    {formatNumber(displayDebtData?.customerDebitAmount || 0)}{' '}
                    vnd
                  </Typography.Text>
                </Flex>
              </Space>

              <Divider style={{ margin: '12px 0' }} />

              <Space direction="vertical" className="w-full">
                <Typography.Text className="text-base font-bold">
                  Hạn mức còn lại
                </Typography.Text>
                <Flex justify={'space-between'} gap={10} className="w-full">
                  <p className="max-w-max">Lần xuất bán Q10</p>
                  <Typography.Text className="flex-1 text-right">
                    {formatCurrency({
                      value: Number(
                        Number(
                          displayDebtData?.goldThreadSalesLimit || 0,
                        ).toFixed(4),
                      ),
                      format: 4,
                    })}
                  </Typography.Text>
                </Flex>
                <Flex justify={'space-between'} gap={10} className="w-full">
                  <p className="max-w-max">Lần xuất bán VND</p>
                  <Typography.Text className="flex-1 text-right">
                    {formatNumber(displayDebtData?.amountSalesLimit || 0)} vnd
                  </Typography.Text>
                </Flex>
                <Flex justify={'space-between'} gap={10} className="w-full">
                  <p className="max-w-max">Q10</p>
                  <Typography.Text className="flex-1 text-right">
                    {formatCurrency({
                      value: Number(
                        Number(displayDebtData?.remainingQ10 || 0).toFixed(4),
                      ),
                      format: 4,
                    })}
                  </Typography.Text>
                </Flex>

                <Flex justify={'space-between'} gap={10} className="w-full">
                  <p className="max-w-max">Truyền thống</p>
                  <Typography.Text className="flex-1 text-right">
                    {formatNumber(displayDebtData?.traditionalLimit || 0)} vnd
                  </Typography.Text>
                </Flex>
                <Flex justify={'space-between'} gap={10} className="w-full">
                  <p className="max-w-max">Cao cấp</p>
                  <Typography.Text className="flex-1 text-right">
                    {formatNumber(displayDebtData?.premiumLimit || 0)} vnd
                  </Typography.Text>
                </Flex>
                <Flex justify={'space-between'} gap={10} className="w-full">
                  <p className="max-w-max">
                    Cao cấp và
                    <br />
                    tiền công truyền thống
                  </p>
                  <Typography.Text className="flex-1 text-right">
                    {formatNumber(
                      displayDebtData?.premiumTraditionalLimit || 0,
                    )}{' '}
                    vnd
                  </Typography.Text>
                </Flex>
                <Flex justify={'space-between'} gap={10} className="w-full">
                  <p className="max-w-max">Tổng</p>
                  <Typography.Text className="flex-1 text-right">
                    {formatNumber(displayDebtData?.totalLimit || 0)} vnd
                  </Typography.Text>
                </Flex>
              </Space>

              <div style={{ marginTop: '12px' }}>
                <Typography.Link
                  style={{ color: '#1890ff', fontSize: '14px' }}
                  onClick={() => {
                    if (customerIdSelected) {
                      fetchDebtDetail();
                    } else {
                      toastError('Vui lòng chọn khách hàng');
                    }
                  }}
                >
                  Chi tiết nợ &gt;
                </Typography.Link>
              </div>
            </Space>
          </div>
          <Content>
            <Space className="w-full" direction="vertical">
              <Row justify={'space-between'}>
                <Col>
                  <p>Ngày tạo </p>
                </Col>
                <Col>
                  <p>
                    {isAddState && !paymentSummaryDetail.createDate
                      ? '-'
                      : formatDate(paymentSummaryDetail.createDate)}
                  </p>
                </Col>
              </Row>
              <Flex justify={'space-between'} gap={10}>
                <p className="max-w-max">Người tạo</p>
                <Typography.Text
                  ellipsis={{ tooltip: paymentSummaryDetail?.createName }}
                  className="flex-1 text-right"
                >
                  {isAddState && !paymentSummaryDetail.createName
                    ? '-'
                    : paymentSummaryDetail.createName}
                </Typography.Text>
              </Flex>
              <Row justify={'space-between'}>
                <Col>
                  <p>Ngày cập nhật</p>
                </Col>
                <Col>
                  <p>
                    {isAddState && !paymentSummaryDetail.modifyDate
                      ? '-'
                      : formatDate(paymentSummaryDetail.modifyDate)}
                  </p>
                </Col>
              </Row>
              <Flex justify={'space-between'} gap={10}>
                <p className="max-w-max">Người cập nhật</p>
                <Typography.Text
                  ellipsis={{ tooltip: paymentSummaryDetail?.modifyName }}
                  className="flex-1 text-right"
                >
                  {isAddState && !paymentSummaryDetail.modifyName
                    ? '-'
                    : paymentSummaryDetail.modifyName}
                </Typography.Text>
              </Flex>
            </Space>
          </Content>
        </Col>
        <Col span={18}>
          <Collapse
            // defaultActiveKey={['3']}
            defaultActiveKey={['1', '2', '3']}
            ghost
            items={items}
            expandIcon={({ isActive }) => (
              <CaretRightOutlined
                rotate={isActive ? 90 : 0}
                style={{
                  fontSize: '18px',
                  color: isActive ? '#0077cc' : '#003056',
                }}
              />
            )}
          />
        </Col>
      </Row>
      <ModalExportHDDT
        isOpen={isOpenModalHDDT}
        setIsOpen={setIsOpenModalHDDT}
        sumPaymentId={sumPaymentId}
        customerId={customerIdSelected}
        isEligibleToLock={isEligibleForBilling}
        onCheckDebt={(customerId: string) => {
          setCheckDebtForBilling(true);
          checkDebt(customerId);
        }}
      />

      {/* Debt Check Modal */}
      <Modal
        title="Kết quả kiểm tra công nợ"
        open={isDebtCheckModalOpen}
        onCancel={() => setIsDebtCheckModalOpen(false)}
        footer={[
          <AntButton key="close" onClick={() => setIsDebtCheckModalOpen(false)}>
            Đóng
          </AntButton>,
        ]}
        width={600}
      >
        {debtCheckResult ? (
          <div>
            {/* Hiển thị lỗi từ THTT check */}
            {debtCheckResult?.hasTHTTError === true ? (
              <div style={{ marginBottom: '16px', textAlign: 'center' }}>
                <Typography.Text
                  strong
                  style={{ fontSize: '16px', color: '#ff4d4f' }}
                >
                  ❌{' '}
                  {debtCheckResult.thttCheck?.message ||
                    'Có lỗi trong kiểm tra THTT'}
                </Typography.Text>
                <div style={{ marginTop: '16px', textAlign: 'left' }}>
                  {debtCheckResult.thttCheck?.data?.map(
                    (item: any, index: number) => (
                      <div
                        key={index}
                        style={{
                          marginBottom: '8px',
                          padding: '8px',
                          backgroundColor: '#fff2f0',
                          border: '1px solid #ffccc7',
                          borderRadius: '4px',
                        }}
                      >
                        <Typography.Text strong>
                          Đơn hàng: {item.orderId}
                        </Typography.Text>
                        <br />
                        <Typography.Text>{item.message}</Typography.Text>
                      </div>
                    ),
                  )}
                </div>
              </div>
            ) : (
              <div style={{ marginBottom: '16px', textAlign: 'center' }}>
                <Typography.Text strong style={{ fontSize: '16px' }}>
                  {isEligibleToLock
                    ? '✅ Đủ điều kiện tạo công nợ mới'
                    : '❌ Không thể tạo công nợ mới'}
                </Typography.Text>
              </div>
            )}

            {/* Chỉ hiển thị chi tiết công nợ khi không có lỗi THTT */}
            {debtCheckResult?.hasTHTTError !== true && (
              <>
                <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                  <Col span={12}>
                    <Card size="small" title="Tiền VND">
                      <div>
                        <Typography.Text strong>
                          Công nợ hiện tại:
                        </Typography.Text>
                        <br />
                        <Typography.Text style={{ color: '#FF4D4F' }}>
                          {formatNumber(displayDebtData?.pnjpDebitAmount || 0)}{' '}
                          (PNJP)
                        </Typography.Text>
                        <br />
                        <Typography.Text style={{ color: '#52C41A' }}>
                          +{' '}
                          {formatNumber(
                            displayDebtData?.customerDebitAmount || 0,
                          )}{' '}
                          (KH)
                        </Typography.Text>
                        <Divider style={{ margin: '8px 0' }} />
                        <Typography.Text strong>
                          Tổng:{' '}
                          {formatNumber(
                            (displayDebtData?.pnjpDebitAmount || 0) +
                              (displayDebtData?.customerDebitAmount || 0),
                          )}{' '}
                          VND
                        </Typography.Text>
                      </div>
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card size="small" title="Vàng Q10">
                      <div>
                        <Typography.Text strong>
                          Công nợ hiện tại:
                        </Typography.Text>
                        <br />
                        <Typography.Text style={{ color: '#FF4D4F' }}>
                          {formatNumber(displayDebtData?.pnjpDebitQ10 || 0)}{' '}
                          (PNJP)
                        </Typography.Text>
                        <br />
                        <Typography.Text style={{ color: '#52C41A' }}>
                          +{' '}
                          {formatNumber(displayDebtData?.customerDebitQ10 || 0)}{' '}
                          (KH)
                        </Typography.Text>
                        <Divider style={{ margin: '8px 0' }} />
                        <Typography.Text strong>
                          Tổng:{' '}
                          {formatNumber(
                            (displayDebtData?.pnjpDebitQ10 || 0) +
                              (displayDebtData?.customerDebitQ10 || 0),
                          )}{' '}
                          Q10
                        </Typography.Text>
                      </div>
                    </Card>
                  </Col>
                </Row>

                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Card size="small" title="Hạn mức VND">
                      <Typography.Text>
                        Còn lại:{' '}
                        {(() => {
                          const remainingLimit =
                            debtCalculationData?.remainingAmountLimit || 0;
                          const newDebt = Math.abs(
                            debtCalculationData?.newDebtAmount || 0,
                          );
                          const result = Math.max(0, remainingLimit - newDebt);
                          return formatNumber(result);
                        })()}{' '}
                        VND
                      </Typography.Text>
                      <Divider style={{ margin: '8px 0' }} />
                      <Typography.Text strong>
                        Tối đa:{' '}
                        {formatNumber(debtCalculationData?.maxAmountLimit || 0)}{' '}
                        VND
                      </Typography.Text>
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card size="small" title="Hạn mức Q10">
                      <Typography.Text>
                        Còn lại:{' '}
                        {formatNumber(
                          Math.max(
                            0,
                            (debtCalculationData?.remainingQ10Limit || 0) -
                              Math.abs(debtCalculationData?.newDebtQ10 || 0),
                          ),
                        )}{' '}
                        Q10
                      </Typography.Text>
                      {detailData?.type === 'DEB10_01' && (
                        <>
                          <Divider style={{ margin: '8px 0' }} />
                          <Typography.Text strong>
                            Tối đa:{' '}
                            {formatNumber(
                              debtCalculationData?.maxQ10Limit || 0,
                            )}{' '}
                            Q10
                          </Typography.Text>
                        </>
                      )}
                    </Card>
                  </Col>
                </Row>

                <Divider />
                <Typography.Text
                  type="success"
                  style={{
                    fontWeight: 'bold',
                    textAlign: 'center',
                    display: 'block',
                  }}
                >
                  {isEligibleToLock
                    ? '✅ Có thể tiến hành khoá bảng THTT'
                    : '⚠️ Cần điều chỉnh công nợ trước khi khoá THTT'}
                </Typography.Text>
              </>
            )}
          </div>
        ) : (
          <Typography.Text>Đang kiểm tra...</Typography.Text>
        )}
      </Modal>

      {/* Lock THTT Modal */}
      <Modal
        title={lockAction === 'unlock' ? 'Mở khoá bảng THTT' : 'Khoá bảng THTT'}
        open={isLockModalOpen}
        onCancel={() => setIsLockModalOpen(false)}
        footer={[
          <AntButton key="back" onClick={() => setIsLockModalOpen(false)}>
            Quay lại
          </AntButton>,
          <AntButton
            key="confirm"
            type="primary"
            danger={lockAction === 'unlock'}
            onClick={() => {
              toggleLockTHTT();
              setIsLockModalOpen(false);
            }}
            loading={lockingTHTT}
          >
            Xác nhận
          </AntButton>,
        ]}
      >
        <Typography.Text>
          {lockAction === 'unlock'
            ? 'Khi mở khoá các đơn bán hàng đã tạo billing sẽ bị huỷ. Bạn có chắc chắn muốn mở khoá THTT?'
            : 'Bạn có chắc chắn muốn khoá THTT?'}
        </Typography.Text>
      </Modal>

      {/* Debt Detail Modal */}
      <DebtDetailModal
        isOpen={isDebtDetailModalOpen}
        onClose={() => setIsDebtDetailModalOpen(false)}
        ruleData={debtDetailData}
        loading={checkingDebtDetail}
        onRefresh={fetchDebtDetail}
      />
      {amlModalContext}
    </Form>
  );
};

export default DetailPaymentSummaryPage;
